import { useCallback, MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { IconsMap } from '@nx-frontend/shared/ui/icon';
import { toast } from '@nx-frontend/shared/ui/toast';
import { useNavigateToAction, useOpenManageAccessPopup } from '@nx-frontend/shared/hooks';
import { setLastPublishTimeEvent } from '@nx-frontend/shared/store/change-events-store';
import { selectSidebarWidgetEvent } from '@nx-frontend/shared/store/customizer-controlbar-store';
import { duplicateMenuItemFx, getHasDraftDeepFx, getMenuItemByIdFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $virtualPortalNodePaths } from '@nx-frontend/shared/store/virtual-portal-node';
import {
  ContextMenuItemData,
  ContextMenuItemType,
  MenuItem,
  MenuItemType,
  MenuType,
  PORTAL_MENU_TYPE,
  PORTAL_QUERY_ACTION,
} from '@nx-frontend/shared/types';
import { addHttpsToUrl } from '@nx-frontend/shared/utils';
import { useOpenPortalMenuItem } from './open-menu-item';

export const useOpenPortalMenuItemContextMenu = () => {
  const t = useTranslations('customizer');
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const openManageAccess = useOpenManageAccessPopup();

  const [virtualNodePaths, portalSettings] = useUnit([$virtualPortalNodePaths, $portalSettings]);
  const [
    openContextMenu,
    getHasDraftDeep,
    duplicateMenuItem,
    setLastPublishTime,
    getMenuItemById,
    selectSidebarWidget,
  ] = useEvent([
    openContextMenuEvent,
    getHasDraftDeepFx,
    duplicateMenuItemFx,
    setLastPublishTimeEvent,
    getMenuItemByIdFx,
    selectSidebarWidgetEvent,
  ]);
  const openPortalMenuItem = useOpenPortalMenuItem();
  const navigateToAction = useNavigateToAction();
  return useCallback(
    async (item: MenuItem, menuType: PORTAL_MENU_TYPE, event: MouseEvent, useMousePos?: boolean) => {
      const items: ContextMenuItemData[] = [];
      const isVirtualNodeItem = virtualNodePaths.includes(item.url);
      const parent = item.parentId ? await getMenuItemById({ id: item.parentId, menuType }) : null;
      const isProjection = !!item.originGlobalId;
      const isParentProjection = !!parent?.originGlobalId;

      const manageAccessAvailable =
        !item.parentId.length &&
        item.type !== MenuItemType.HOME &&
        menuType === MenuType.PORTAL_SIDEBAR &&
        !(item.isOrigin === false || item.isOrigin === true);

      const host = 'https://' + portalSettings.domain;

      const openInPortalViewAction: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        title: t('customizer-open_in_portal_view'),
        icon: IconsMap.LEFT_PANEL,
        isSeparator: item.type !== MenuItemType.HOME,
        action: () => {
          window.open(addHttpsToUrl(portalSettings.domain + item.url));
        },
      };

      const editAction: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        title: t('customizer-edit'),
        icon: IconsMap.EDIT,
        action: () => {
          openPortalMenuItem(item, menuType);
          selectSidebarWidget(menuType);
        },
      };

      const copyLinkAction: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        title: t('customizer-copy_link'),
        icon: IconsMap.LINK_1,
        isSeparator: item.type !== MenuItemType.HOME,
        action: () => {
          if (item.url) {
            if (item.url.startsWith('https') || item.url.startsWith('http')) {
              navigator.clipboard.writeText(item.url);
            } else {
              navigator.clipboard.writeText(host + item.url);
            }
          }
        },
      };

      const openOriginalLinkAction: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        title: t('customizer-open_original_link'),
        icon: IconsMap.LOG_OUT_2,
        isSeparator: true,
        action: () => {
          if (item.url) {
            window.open(item.url, '_blank');
          }
        },
      };

      const duplicateAction: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        title: t('customizer-duplicate'),
        icon: IconsMap.COPY,
        action: async () => {
          const hasDraft = await getHasDraftDeep({
            menuType: menuType,
            parentId: item.id,
            includeParent: true,
          });

          if (item.isDraft || hasDraft) {
            toast({ title: t('customizer-unpublished_items_toast') });
            return false;
          }
          toast({ title: 'Duplication in progress.' });
          try {
            await duplicateMenuItem({
              srcId: item.id,
              newName: item.name,
              portalId: portalSettings.id,
            });
            const params = new URLSearchParams(searchParams?.toString());
            const time = new Date().getTime();

            setLastPublishTime(time);

            params.set('ts', time.toString());
            router.push(pathname + '?' + params.toString());

            toast({ title: t('customizer-item_duplicated_toast', { itemName: item.name }) });
          } catch (err) {
            console.error('Error duplicating menu item:', err);
            toast({ title: 'Error duplicating menu item' });
          }
        },
      };

      const deleteAction: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        title: t('customizer-delete'),
        isSeparator: manageAccessAvailable,
        icon: IconsMap.TRASH,
        action: () => {
          navigateToAction({
            action: PORTAL_QUERY_ACTION.DELETE,
            menuType,
            itemId: item.id,
          });
        },
      };

      const manageAccessAction: ContextMenuItemData = {
        type: ContextMenuItemType.DEFAULT,
        title: t('customizer-manage_access'),
        icon: IconsMap.PERSONS,
        additionalStyles: '!text-cyan-900 font-semibold',
        iconStyles: '!text-cyan-900 ',

        action: () => {
          if (openManageAccess) {
            openManageAccess({
              id: item.id,
              menuType,
              resourceUrl: `${host}${item.url}`,
            });
          }
        },
      };

      if (item.type === MenuItemType.LINK) {
        items.push(openOriginalLinkAction);
      } else {
        items.push(openInPortalViewAction);
      }

      items.push(editAction);

      if (!isVirtualNodeItem) {
        items.push(copyLinkAction);
      }

      if (
        item.type !== MenuItemType.HOME &&
        item.type !== MenuItemType.PORTAL_PROCESS &&
        item.positionType === 'sidebar' &&
        !isProjection
      ) {
        items.push(duplicateAction);
      }

      if (item.type !== MenuItemType.HOME && !isParentProjection) {
        items.push(deleteAction);
      }

      if (manageAccessAvailable) {
        items.push(manageAccessAction);
      }

      openContextMenu({
        items,
        position: calculateContextMenuPosition(
          event.target,
          4,
          undefined,
          useMousePos ? { x: event.clientX, y: event.clientY } : undefined
        ),
      });
    },
    [
      virtualNodePaths,
      getMenuItemById,
      portalSettings.domain,
      portalSettings.id,
      t,
      openContextMenu,
      openPortalMenuItem,
      selectSidebarWidget,
      getHasDraftDeep,
      duplicateMenuItem,
      searchParams,
      setLastPublishTime,
      router,
      pathname,
      navigateToAction,
      openManageAccess,
    ]
  );
};
